import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import {
  CmpMemberContactAccountActionEnum as Action,
  GetMyCmpMemberContactAccountsQuery,
  useGetMyCmpMemberContactAccountsQuery,
  useManageMyCmpMemberContactAccountMutation,
} from '../../../../graphql/types';
import { PhoneNumberFormatCustom } from '../../../common/utilities/PhoneNumberFormat';
import {
  BILLING_NOTICE, buildCredentialsText, CMP_CONTACT_CARDS, CmpContactCard, CmpContactFields, CONTACT_FIELD_KEYS,
  CONTACTS_NOT_IN_DIRECTORY, formatContactDate, getCardFields, getPrimaryContactFields, GRANT_LABEL,
  INCOMPLETE_CONTACT_HINT, isCompleteContact, isSameAsPrimary, SAME_AS_PRIMARY_HINT, SAME_AS_PRIMARY_WARNING, SAVE_FIRST_HINT,
} from './cmpContactCards';

// Wave C ACON-2: Company Contacts cards for the Primary Contact (design
// ACC:L354-641 cards, L1386-1432 Cancel Access and Send Credentials modals).

type Account = GetMyCmpMemberContactAccountsQuery['getMyCmpMemberContactAccounts']['accounts'][number];
type Formik = {
  values: unknown;
  errors: unknown;
  touched: unknown;
  submitCount: number;
  setFieldValue: (field: string, value: unknown) => void;
  handleChange: (event: React.ChangeEvent<unknown>) => void;
};
type Classes = Record<'formSection' | 'sectionHeader' | 'sectionTitle' | 'sectionHelperText' | 'formRow' | 'fieldLabel' | 'companyInput' | 'requiredAsterisk', string>;
type CredentialModal = { card: CmpContactCard; name: string; email: string; account: Account; warn: 'loggedIn' | 'resend' | null; done: boolean };

const RED = '#ED1C24';
const GOLD = '#E1A731';
const TEXT = '#0A0A0A';
const GREEN = '#10B981';
const operationContext = { preserveErrorForCaller: true };
const errorMessage = (error: unknown, fallback: string) =>
  (error as { graphQLErrors?: { message?: string }[] }).graphQLErrors?.[0]?.message || fallback;

const modalButton = (background: string): React.CSSProperties => ({
  flex: 1, background, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '13px 0', fontFamily: "'DM Sans', sans-serif",
  fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%',
});
const ModalIcon = ({ background, children }: { background: string; children: React.ReactNode }) => (
  <div style={{ width: 64, height: 64, borderRadius: '50%', background, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>{children}</div>
);
const SendIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
);

const SmallModal = ({ onClose, label, children }: { onClose: () => void; label: string; children: React.ReactNode }) => (
  <Dialog open onClose={onClose} aria-label={label} maxWidth={false}
    PaperProps={{ style: { width: 'min(420px, 92vw)', borderRadius: 16, margin: 0, position: 'relative', padding: '32px 32px 28px', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
    <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%',
      background: RED, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✕</button>
    {children}
  </Dialog>
);

export default function CmpCompanyContactCards({ formik, classes, chamberName, timeZone, savedRevision }: {
  formik: Formik; classes: Classes; chamberName: string; timeZone?: string | null;
  /** Changes after each profile save, so the access rows reload. */
  savedRevision?: number | string | null;
}) {
  const accountsQuery = useGetMyCmpMemberContactAccountsQuery({ fetchPolicy: 'network-only', context: operationContext });
  const { refetch } = accountsQuery;
  useEffect(() => { if (savedRevision) void refetch(); }, [savedRevision, refetch]);
  const [manage] = useManageMyCmpMemberContactAccountMutation({ context: operationContext });
  const [busyContact, setBusyContact] = useState<string>();
  const [hint, setHint] = useState<Record<string, string | undefined>>({});
  const [sameWarning, setSameWarning] = useState<Record<string, boolean>>({});
  const [revoke, setRevoke] = useState<{ card: CmpContactCard; name: string; done: boolean }>();
  const [credentials, setCredentials] = useState<CredentialModal>();
  const [modalError, setModalError] = useState<string>();
  const beforeSame = useRef<Record<string, CmpContactFields>>({});

  const data = accountsQuery.data?.getMyCmpMemberContactAccounts;
  const accounts = data?.accounts || [];
  const primary = getPrimaryContactFields(formik.values);
  const setCardHint = (contactId: string, value?: string) => setHint(current => ({ ...current, [contactId]: value }));

  const runAction = async (card: CmpContactCard, action: Action) => {
    if (!data) throw new Error('Contact access is still loading. Try again in a moment.');
    const result = await manage({ variables: { input: { contactId: card.contactId, action,
      expectedMemberUpdatedAtUtcMilli: data.memberUpdatedAtUtcMilli } } });
    await accountsQuery.refetch();
    return result.data?.manageMyCmpMemberContactAccount;
  };

  const setCardFields = (card: CmpContactCard, fields: CmpContactFields) =>
    CONTACT_FIELD_KEYS.forEach(key => formik.setFieldValue(card.fieldNames[key], fields[key]));

  const toggleSame = (card: CmpContactCard, fields: CmpContactFields, same: boolean) => {
    if (!primary.name.trim() || !primary.email.trim()) { setSameWarning(current => ({ ...current, [card.contactId]: true })); return; }
    setSameWarning(current => ({ ...current, [card.contactId]: false }));
    if (same) { setCardFields(card, beforeSame.current[card.contactId] || { name: '', title: '', email: '', phone: '', ext: '', mobile: '' }); return; }
    beforeSame.current[card.contactId] = fields;
    setCardFields(card, primary);
  };

  const grant = async (card: CmpContactCard, fields: CmpContactFields, account: Account | undefined, same: boolean) => {
    const granted = account?.enabled === true;
    if (granted) { setRevoke({ card, name: fields.name || 'This contact', done: false }); return; }
    if (!isCompleteContact(fields)) { setCardHint(card.contactId, INCOMPLETE_CONTACT_HINT); return; }
    if (same) { setCardHint(card.contactId, SAME_AS_PRIMARY_HINT); return; }
    const saved = !!account && account.email.trim().toLowerCase() === fields.email.trim().toLowerCase() &&
      (account.name || '').trim() === fields.name.trim();
    if (!saved) { setCardHint(card.contactId, SAVE_FIRST_HINT); return; }
    setBusyContact(card.contactId); setCardHint(card.contactId);
    try { await runAction(card, Action.Enable); }
    catch (error) { setCardHint(card.contactId, errorMessage(error, 'Access could not be granted. Refresh the page and try again.')); }
    finally { setBusyContact(undefined); }
  };

  const confirmRevoke = async () => {
    if (!revoke) return;
    setModalError(undefined);
    try { await runAction(revoke.card, Action.Disable); setRevoke({ ...revoke, done: true }); }
    catch (error) { setModalError(errorMessage(error, 'Access could not be cancelled. Refresh the page and try again.')); }
  };

  const sendCredentials = async () => {
    if (!credentials) return;
    setModalError(undefined);
    try {
      const action = credentials.account.credentialsSentAtUtcMilli ? Action.ResetPassword : Action.SendCredentials;
      const result = await runAction(credentials.card, action);
      if (!result?.deliveryConfirmed) throw new Error(result?.trackingWarning || 'Email delivery could not be confirmed. Do not send again. Ask your chamber to check delivery first.');
      setCredentials({ ...credentials, done: true });
      if (result.trackingWarning) setModalError(result.trackingWarning);
    } catch (error) {
      setModalError(errorMessage(error, (error as Error).message || 'Credentials could not be sent. Refresh the page and try again.'));
    }
  };

  const downloadCredentials = async () => {
    if (!credentials) return;
    setModalError(undefined);
    try {
      const result = await runAction(credentials.card, Action.DownloadCredentials);
      const prepared = result?.credentials;
      if (!prepared) throw new Error('Credentials could not be prepared. Refresh the page and try again.');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([buildCredentialsText({ chamberName, name: credentials.name, email: prepared.email,
        password: prepared.password, loginUrl: window.location.origin })], { type: 'text/plain' }));
      link.download = `Member Dashboard Credentials - ${credentials.name || 'Contact'}.txt`;
      document.body.appendChild(link); link.click(); link.remove();
      if (result?.trackingWarning) setModalError(result.trackingWarning);
    } catch (error) {
      setModalError(errorMessage(error, (error as Error).message || 'Credentials could not be prepared. Refresh the page and try again.'));
    }
  };

  const pathValue = (source: unknown, path: string): unknown => path.split('.')
    .reduce<unknown>((value, key) => (value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined), source);
  const input = (card: CmpContactCard, key: keyof CmpContactFields, placeholder: string, phone = false) => {
    const path = card.fieldNames[key];
    const error = pathValue(formik.errors, path);
    const shown = typeof error === 'string' && (!!pathValue(formik.touched, path) || formik.submitCount > 0) ? error : undefined;
    return (
      <TextField placeholder={placeholder} className={classes.companyInput} variant="outlined" name={path}
        inputProps={{ autoComplete: 'new-password', 'aria-label': `${card.title} ${key}` }}
        value={getCardFields(formik.values, card)[key]} onChange={formik.handleChange}
        error={!!shown} helperText={shown}
        InputProps={phone ? { inputComponent: PhoneNumberFormatCustom as never } : undefined} />
    );
  };
  const billingInput = (field: string, label: string, placeholder: string) => (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <TextField placeholder={placeholder} className={classes.companyInput} variant="outlined" name={`cmpBillingAddress.${field}`}
        inputProps={{ autoComplete: 'new-password', 'aria-label': `Billing ${label}` }}
        value={((formik.values as { cmpBillingAddress?: Record<string, string> }).cmpBillingAddress || {})[field] || ''}
        onChange={formik.handleChange} />
    </div>
  );

  return (
    <>
      {accountsQuery.error && (
        <p role="alert" style={{ maxWidth: 1096, margin: '0 auto', color: RED, fontWeight: 600 }}>
          {errorMessage(accountsQuery.error, 'Member Dashboard access for your contacts could not be loaded.')}{' '}
          <button type="button" onClick={() => accountsQuery.refetch()} style={{ textDecoration: 'underline', background: 'none', border: 'none', color: RED, cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </p>
      )}
      {CMP_CONTACT_CARDS.map(card => {
        const fields = getCardFields(formik.values, card);
        const account = accounts.find(row => row.contactId === card.contactId);
        const same = isSameAsPrimary(fields, primary);
        const granted = account?.enabled === true;
        const blocked = account?.usesLegacyMemberAccount
          ? account.credentialActionsBlockedReason || 'This contact uses the company login. Ask your chamber to manage it.'
          : account?.credentialActionsBlockedReason || null;
        const complete = isCompleteContact(fields);
        const busy = busyContact === card.contactId;
        const sentAt = granted ? formatContactDate(account?.credentialsSentAtUtcMilli, timeZone) : null;
        const lastLogin = granted ? formatContactDate(account?.lastDashboardLoginAtUtcMilli, timeZone) : null;
        const canSend = granted && !blocked && !busy;
        // As on the staff page, a note stops new access but never stops removing
        // access (for example a login connected from another chamber, M5).
        const canRevoke = granted && !account?.usesLegacyMemberAccount;
        return (
          <div key={card.contactId} className={classes.formSection} data-testid={`cmp-contact-${card.contactId}`}>
            <div className={classes.sectionHeader}><p className={classes.sectionTitle}>{card.title}</p></div>
            <p className={classes.sectionHelperText}>{card.intro}</p>
            <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>{CONTACTS_NOT_IN_DIRECTORY}</p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 16px', cursor: 'pointer' }}>
              <input type="checkbox" checked={same} onChange={() => toggleSame(card, fields, same)}
                style={{ width: 18, height: 18, margin: '1px 0 0', flexShrink: 0, accentColor: GOLD, cursor: 'pointer' } as React.CSSProperties} />
              <span style={{ fontSize: 14, color: TEXT, lineHeight: 1.55 }}>Check here if same as primary contact</span>
            </label>
            {sameWarning[card.contactId] && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED, margin: '-8px 0 16px', paddingLeft: 28 }}>{SAME_AS_PRIMARY_WARNING}</div>}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 24px', cursor: complete && (!blocked || canRevoke) ? 'pointer' : 'not-allowed' }}>
              <input type="checkbox" checked={granted} disabled={busy || (!!blocked && !canRevoke)} onChange={() => grant(card, fields, account, same)}
                aria-label={`Grant ${card.title} access`}
                style={{ width: 18, height: 18, margin: '1px 0 0', flexShrink: 0, accentColor: GOLD, opacity: complete && (!blocked || canRevoke) ? 1 : 0.5 } as React.CSSProperties} />
              <span style={{ fontSize: 14, lineHeight: 1.55, color: complete && (!blocked || canRevoke) ? TEXT : '#9A9A9A' }}>{GRANT_LABEL}</span>
            </label>
            {(hint[card.contactId] || blocked) && (
              <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED, margin: '-16px 0 24px', paddingLeft: 28, lineHeight: 1.5 }}>{hint[card.contactId] || blocked}</div>
            )}
            <div className={classes.formRow}>
              <div>
                <div className={classes.fieldLabel}><span>Contact Name<span className={classes.requiredAsterisk}>*</span></span></div>
                {input(card, 'name', 'Enter Contact Name')}
              </div>
              <div>
                <div className={classes.fieldLabel}>Title</div>
                {input(card, 'title', 'Enter Contact Person Title')}
              </div>
            </div>
            <div className={classes.formRow}>
              <div>
                <div className={classes.fieldLabel}><span>Contact Email<span className={classes.requiredAsterisk}>*</span></span></div>
                {input(card, 'email', 'Enter Contact Email')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
                <div style={{ flex: '1 1 150px', minWidth: 0 }}><div className={classes.fieldLabel} style={{ whiteSpace: 'nowrap' }}>Company Number</div>{input(card, 'phone', 'Company Number', true)}</div>
                <div style={{ flex: '0 0 80px' }}><div className={classes.fieldLabel}>Ext.</div>{input(card, 'ext', '0000')}</div>
                <div style={{ flex: '1 1 150px', minWidth: 0 }}><div className={classes.fieldLabel} style={{ whiteSpace: 'nowrap' }}>Mobile Number</div>{input(card, 'mobile', 'Mobile Number', true)}</div>
              </div>
            </div>
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #CFCFCF' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <button type="button" disabled={!canSend} data-testid={`send-credentials-${card.contactId}`}
                    onClick={() => account && setCredentials({ card, name: fields.name, email: fields.email, account, done: false,
                      warn: account.credentialsSentAtUtcMilli && account.lastDashboardLoginAtUtcMilli ? 'loggedIn' : account.credentialsSentAtUtcMilli ? 'resend' : null })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 22px',
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: canSend ? 'pointer' : 'not-allowed',
                      background: !canSend ? '#CFCFCF' : lastLogin ? '#9E9E9E' : GOLD }}>
                    <SendIcon />Send Credentials
                  </button>
                  <span style={{ fontSize: 14, color: TEXT }}>Last Sent: {sentAt || 'Never'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 41 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: lastLogin ? GREEN : '#9A9A9A' }} />
                  <span style={{ fontSize: 16, fontWeight: 600, color: lastLogin ? TEXT : '#9A9A9A' }}>{lastLogin ? `Last Login: ${lastLogin}` : 'Never Logged In'}</span>
                </div>
                {canRevoke && (
                  <button type="button" onClick={() => setRevoke({ card, name: fields.name || 'This contact', done: false })}
                    style={{ marginLeft: 'auto', background: RED, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 22px',
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel Access</button>
                )}
              </div>
            </div>
            {card.billingAddress && (
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #CFCFCF' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: TEXT, margin: '0 0 8px' }}>Billing Address</div>
                <p className={classes.sectionHelperText}>{BILLING_NOTICE}</p>
                {billingInput('streetAddress', 'Street Address', 'Enter Street Address, Example: 123 Maple E.g.')}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '0 20px' }}>
                  {billingInput('city', 'City', 'Enter City')}
                  {billingInput('province', 'Province/State', 'Enter Province/State')}
                  {billingInput('postalCode', 'Postal/Zip Code', 'Enter Postal/Zip Code')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '0 20px' }}>
                  {billingInput('country', 'Country', 'Enter Country Name')}
                  <div /><div />
                </div>
              </div>
            )}
          </div>
        );
      })}
      {revoke && (
        <SmallModal label="Cancel Access" onClose={() => { setRevoke(undefined); setModalError(undefined); }}>
          <ModalIcon background={RED}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></ModalIcon>
          {revoke.done ? (<>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Access Denied</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}><b>{revoke.name}</b> no longer has access to your member dashboard. You can set up a new contact and grant them access.</div>
          </>) : (<>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Cancel Access?</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Are you sure you want to cancel member dashboard access for <b>{revoke.name}</b>? Their login credentials will no longer work.</div>
            <button type="button" onClick={confirmRevoke} style={{ ...modalButton(RED), marginTop: 12 }}>Yes</button>
          </>)}
          {modalError && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{modalError}</div>}
        </SmallModal>
      )}
      {credentials && (
        <SmallModal label="Send Credentials" onClose={() => { setCredentials(undefined); setModalError(undefined); }}>
          {credentials.done ? (<>
            <ModalIcon background={GREEN}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 7" /></svg></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Credentials Sent</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Login credentials have been sent to <b>{credentials.email}</b>.</div>
          </>) : credentials.warn ? (<>
            <ModalIcon background={GOLD}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3L2 20h20L12 3z" /><path d="M12 10v4M12 17h.01" /></svg></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{credentials.warn === 'loggedIn' ? 'Contact Will Be Logged Out' : 'Reset Contact Credentials?'}</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-line' }}>
              {credentials.warn === 'loggedIn'
                ? `${credentials.name} last logged in on ${formatContactDate(credentials.account.lastDashboardLoginAtUtcMilli, timeZone)}.\n\nSending new credentials will reset their password and log them out of the Member Dashboard. They must sign in again using the new credentials.`
                : `Credentials have been sent to ${credentials.name}, but they have not logged in yet.\n\nSending new credentials will reset their password and invalidate the previously sent credentials.`}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Do you want to continue?</div>
            <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 12 }}>
              <button type="button" onClick={() => setCredentials({ ...credentials, warn: null })} style={modalButton(GOLD)}>Yes, Continue</button>
              <button type="button" onClick={() => setCredentials(undefined)} style={modalButton(TEXT)}>No</button>
            </div>
          </>) : (<>
            <ModalIcon background={GOLD}><SendIcon size={28} /></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Send Credentials</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Member Dashboard login credentials will be emailed to <b>{credentials.name}</b> at <b>{credentials.email}</b>.</div>
            <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 12 }}>
              <button type="button" onClick={sendCredentials} style={modalButton(GOLD)}>Send Credentials</button>
            </div>
            <button type="button" onClick={downloadCredentials} style={modalButton(TEXT)}>Download Credentials</button>
          </>)}
          {modalError && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{modalError}</div>}
        </SmallModal>
      )}
    </>
  );
}
